// COMPONENTS
import { memo, useCallback, useEffect } from 'react';
import DiscardConfirmation, {
  useDiscardConfirmation,
} from 'components/DiscardConfirmation';
// i18N
import { getText } from 'i18n/intl';
import { fetchFields, fetchFieldsGlobalOrder } from 'slices/field.slice';
import { fetchMemberships } from 'slices/memberships.slice';
import { requestObjectTypes } from 'slices/objectTypes.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import { fetchRelationTypes } from 'slices/relationType.slice';
import { fetchReservationModes } from 'slices/reservationMode.slice';
// ACTIONS
import {
  checkTemplateSaveDisabled,
  fetchReservationTemplates,
} from 'slices/reservationTemplate.slice';
import { useAppDispatch } from 'slices/store';
import { DefaultContentLayout } from '@timeedit/ui-components';
import ReservationTemplateDrawer from './ReservationTemplateDrawer';
import ReservationTemplateList from './ReservationTemplateList';

// Hooks
const ReservationTemplateTable = memo(ReservationTemplateList);

const ReservationTemplatesPage = () => {
  const dispatch = useAppDispatch();

  const [
    openDiscardDialog,
    onCloseDiscardDialog,
    confirmFn,
    confirmDiscardChanged,
  ] = useDiscardConfirmation();

  useEffect(() => {
    dispatch(fetchReservationTemplates({ active: 'ALL', templateType: 'ALL' }));
    dispatch(fetchReservationModes());
    dispatch(fetchFields());
    dispatch(fetchRelationTypes());
    dispatch(fetchMemberships());
    dispatch(requestObjectTypes());
    dispatch(fetchOrganizationHierarchy());
    dispatch(fetchFieldsGlobalOrder());
  }, []);

  const confirmDiscardTemplateChanges = useCallback(
    (isFirstDrawer: boolean, actionFn: () => void) => {
      const templateSaveDisabled = dispatch(
        checkTemplateSaveDisabled(isFirstDrawer)
      );
      if (!templateSaveDisabled) {
        confirmDiscardChanged(() => {
          actionFn();
        });
      } else {
        actionFn();
      }
    },
    []
  );

  return (
    <DefaultContentLayout
      title={getText('system_reservation_template_title')}
      subtitle={getText('system_reservation_template_subtitle')}
    >
      <div className="inner-content--wrapper">
        <ReservationTemplateTable
          confirmDiscardTemplateChanges={confirmDiscardTemplateChanges}
        />
        <ReservationTemplateDrawer
          confirmDiscardTemplateChanges={confirmDiscardTemplateChanges}
        />
      </div>

      <DiscardConfirmation
        open={openDiscardDialog}
        onClose={onCloseDiscardDialog}
        confirmFn={confirmFn}
      />
    </DefaultContentLayout>
  );
};

export default ReservationTemplatesPage;
